import { FunctionComponent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";

import { isIPv4 } from "is-ip";

import AppLogo from "@/assets/images/favicon.png";

import { ROUTE_CONFIG } from "@/routes/config";

import { InputField } from "@/components/InputField/InputField";

import { Button } from "@/components/Button/Button";
import { ButtonSize } from "@/components/Button/types";
import { useAppSelector } from "@/redux/hooks";
import { authSelector } from "@/pages/Public/redux/auth.slice";

import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";

import {
	LinkedinProfileCombobox,
	LinkedinProfileSelection,
} from "@/pages/Private/pages/Onboarding/components/LinkedinProfileCombobox";

import { OnboardingSettingsV3, OnboardingSettingsV3Schema } from "../schema/onboarding";

import {
	useLocationMutation,
	useSaveSlimOnboardingSettingsMutation,
	useUpdateMeMutation,
} from "../../../redux/account/account.api";

/**
 * The one screen of the slim onboarding (V3, PREC-3719). Whoever ran the full
 * wizard already set this workspace up, so the only thing missing is the user's
 * own LinkedIn profile — picked with the same typeahead as the wizard's Profile
 * step, with a manual URL as the fallback. Everything else they need to learn is
 * the setup guide, which takes over on the dashboard.
 */
export const OnboardingV3Start: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`onboarding.slimStep.${key}`);

	const { user } = useAppSelector(authSelector);

	const initialState: OnboardingSettingsV3 = {
		linkedinUrl: user?.linkedinUrl ?? undefined,
	};

	const [updateLocation] = useLocationMutation();

	useEffect(() => {
		if (!user?.city || !user?.country || !user?.isp || !user?.region || !user?.timezone) {
			fetch("https://api.ipify.org?format=json")
				.then((res) => res.json())
				.then((data) => {
					if (isIPv4(data.ip)) {
						updateLocation(data);
					}
				});
		}
	}, [updateLocation, user?.city, user?.country, user?.isp, user?.region, user?.timezone]);

	const [saveSlimOnboardingSettings, { isLoading, error }] =
		useSaveSlimOnboardingSettingsMutation();
	const [updateMe, { isLoading: isUpdatingMe }] = useUpdateMeMutation();

	const [currentFormState, setCurrentFormState] = useState<OnboardingSettingsV3>(initialState);
	const [selectedProfile, setSelectedProfile] = useState<LinkedinProfileSelection | null>(null);
	const [manualUrlVisible, setManualUrlVisible] = useState(false);

	const navigate = useNavigate();

	const {
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<OnboardingSettingsV3>({
		defaultValues: initialState,
		resolver: zodResolver(OnboardingSettingsV3Schema),
	});

	const formErrors = Object.values(errors).map((error) => error.message) as ERROR_TYPE[];

	const updateLinkedinUrl = (linkedinUrl?: string) => {
		const nextState = { ...currentFormState, linkedinUrl };

		setCurrentFormState(nextState);
		reset(nextState);
	};

	const handleProfileSelect = (selection: LinkedinProfileSelection) => {
		setSelectedProfile(selection);
		setManualUrlVisible(false);
		updateLinkedinUrl(selection.linkedinUrl);
	};

	const handleProfileClear = () => {
		setSelectedProfile(null);
		updateLinkedinUrl(undefined);
	};

	const handleManualEntry = () => {
		setSelectedProfile(null);
		setManualUrlVisible(true);
		updateLinkedinUrl(undefined);
	};

	const onSubmit = async (values: OnboardingSettingsV3) => {
		try {
			await saveSlimOnboardingSettings(values).unwrap();
			// Marking the user onboarded is what releases the app, and this response is
			// what refreshes the cached user — without it the layouts keep redirecting
			// back here.
			await updateMe({ isOnboarded: true }).unwrap();

			navigate(ROUTE_CONFIG.DASHBOARD);
		} catch (err) {
			console.error(err);
		}
	};

	const greeting = user?.firstName
		? t("onboarding.slimStep.titleWithName", { name: user.firstName })
		: ts("title");

	return (
		<div className="w-full flex flex-col items-center justify-center">
			<div className="flex flex-col bg-white px-6 py-10 rounded-[20px] w-[428px] max-w-[100%] shadow-[0px_0px_80px_0px_rgba(171,178,217,0.35)] items-center justify-center mb-6">
				<img alt="logo" className="w-[48px] h-[48px] mb-4" src={AppLogo} />
				<div className="text-center text-md font-bold mb-4">{greeting}</div>
				<div className="text-center text-ssm font-normal mb-8">{ts("subtitle")}</div>

				<div className="text-left text-xs font-normal w-full mb-4">
					<div className="label-styles mb-1">
						{t("onboarding.profileStep.personalLinkedinLabel")}
					</div>
					<LinkedinProfileCombobox
						value={selectedProfile}
						onClear={handleProfileClear}
						onManualEntry={handleManualEntry}
						onSelect={handleProfileSelect}
					/>
				</div>

				{manualUrlVisible && (
					<div className="text-left text-xs font-normal w-full mb-4">
						<InputField
							error={!!errors.linkedinUrl?.message}
							handleChange={(event) => updateLinkedinUrl(event.target.value || undefined)}
							label={t("onboarding.profileStep.personalLinkedinUrlLabel")}
							name="linkedinUrl"
							placeholder="https://www.linkedin.com/in/yourprofile"
							showError={!!errors.linkedinUrl?.message}
							value={currentFormState.linkedinUrl || ""}
						/>
					</div>
				)}

				<div className="w-full">
					<Button
						className="w-full"
						isLoading={isLoading || isUpdatingMe}
						size={ButtonSize.ML}
						title={ts("continue")}
						onClick={handleSubmit(onSubmit)}
					/>
				</div>

				<div className="mt-4">
					{getAllErrors(error, formErrors).length
						? renderErrorMessages(getAllErrors(error, formErrors))
						: null}
				</div>
			</div>
		</div>
	);
};
